import React, { useState } from 'react';
import { formatDublinDateTime } from '../../utils/date';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { useAmendIncidentMutation } from '../../store/api/localApi';
import { IncidentAmendment } from '../../types';
import { useToast } from '../Toast';

function formatDateTime(iso: string): string {
  return formatDublinDateTime(iso);
}

/** One line of human-readable text per amendment kind. Amendments are a
 *  discriminated union (`IncidentAmendment['kind']`) — each kind carries
 *  different fields, so there is no generic "amendment.text" to fall back
 *  on for SEVERITY_CHANGE/TUSLA_REPORTED/WITHDRAWN. */
function describeAmendment(a: IncidentAmendment): string {
  switch (a.kind) {
    case 'NOTE':
      return a.text;
    case 'SEVERITY_CHANGE':
      return `Severity changed ${a.from} → ${a.to}. Reason: ${a.reason}${
        a.reopenedFromClosed ? ' (reopened a closed incident)' : ''
      }`;
    case 'TUSLA_REPORTED':
      return `Reported to Tusla (reference: ${a.reference})`;
    case 'WITHDRAWN':
      return `Incident withdrawn. Reason: ${a.reason}`;
  }
}

interface Props {
  incidentId: string;
  amendments: IncidentAmendment[];
}

/**
 * Append-only correction trail — staff-only (the parent view never sees
 * this). Every entry is immutable once written; the only interaction this
 * component offers is APPENDING a new NOTE, never editing or removing an
 * existing entry, matching the backend's append-only contract
 * (`POST /incidents/{incidentId}/amend` accepts only `{ kind: 'NOTE' }`).
 */
const IncidentAmendmentList: React.FC<Props> = ({ incidentId, amendments }) => {
  const { showError } = useToast();
  const [amendIncident, { isLoading }] = useAmendIncidentMutation();
  const [noteText, setNoteText] = useState('');
  const [showForm, setShowForm] = useState(false);

  const handleAddNote = async () => {
    const text = noteText.trim();
    if (!text) return;
    try {
      await amendIncident({ incidentId, text }).unwrap();
      setNoteText('');
      setShowForm(false);
    } catch (err: unknown) {
      const data =
        err && typeof err === 'object' && 'data' in err
          ? (err as { data: { error?: string; message?: string } | undefined }).data
          : undefined;
      showError(data?.error || data?.message || 'Failed to add note.');
    }
  };

  return (
    <div data-testid="incident-amendment-list" className="bg-white rounded-lg shadow p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
          <PencilSquareIcon className="h-4 w-4 text-gray-400" aria-hidden="true" />
          Amendments
        </h2>
        {!showForm && (
          <button
            type="button"
            data-testid="incident-amendment-add-btn"
            onClick={() => setShowForm(true)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700"
          >
            + Add note
          </button>
        )}
      </div>

      {amendments.length === 0 ? (
        <p className="mt-3 text-sm text-gray-500">No amendments recorded.</p>
      ) : (
        <ul className="mt-3 space-y-3">
          {amendments.map((a) => (
            <li
              key={a.amendmentId}
              data-testid={`incident-amendment-${a.amendmentId}`}
              className="border-l-2 border-gray-200 pl-3"
            >
              <p className="text-sm text-gray-800 whitespace-pre-wrap">{describeAmendment(a)}</p>
              <p className="mt-0.5 text-xs text-gray-400">
                {a.authorName} · {formatDateTime(a.createdAt)}
              </p>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div className="mt-4 border-t border-gray-100 pt-3">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Add a note to this record
          </label>
          <textarea
            data-testid="incident-amendment-note-input"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="A late-arriving fact, a GP follow-up, a correction…"
            className="block w-full rounded-md border-gray-300 h-20 resize-y text-sm"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              data-testid="incident-amendment-cancel-btn"
              onClick={() => {
                setShowForm(false);
                setNoteText('');
              }}
              className="px-3 py-1.5 text-sm text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="incident-amendment-submit-btn"
              onClick={handleAddNote}
              disabled={isLoading || !noteText.trim()}
              className="px-3 py-1.5 text-sm text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Saving…' : 'Add note'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default IncidentAmendmentList;
